import { flatten, isEmpty, values } from "lodash";
import { useAnalyticsModalContext } from "PFApp/analytics/parts/top_actions_panel/analytics_modal_context_provider";
import { ExportType, Frequency } from "PFApp/analytics/types/export_analytics";
import Radios from "PFComponents/radios/radios";
import { Item } from "PFComponents/radios/radios";
import { useTemplates } from "PFCore/hooks/queries/analytics/reports/use_templates";
import React, { JSX, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { decamelizeFilters } from "../../export_fields_utils";
import { getFrequencyByValue } from "../../helpers";
import { ReportTypeSelector } from "./report_type_selector";
import css from "./template_and_report_type_tab.module.scss";
import { TemplateSelector } from "./template_selector";

export const TemplateAndReportTypeTab = (): JSX.Element => {
  const {
    updateData,
    state: {
      data: { exportType, selectedTemplate }
    }
  } = useAnalyticsModalContext();
  const { data: templates } = useTemplates();
  const { t } = useTranslation("insights", { keyPrefix: "exportWizard" });

  const items: Item<ExportType>[] = useMemo(
    () => [
      {
        id: ExportType.Template,
        displayElement: (
          <div className={css.templatePicker}>
            <p>{t("yesIllPickATemplate")}</p>
            <TemplateSelector disabled={exportType === ExportType.Custom} templates={templates} />
          </div>
        ),
        disabled: isEmpty(templates?.entries)
      },
      {
        id: ExportType.Custom,
        displayElement: (
          <div className={css.customOption}>
            {t("noIDontWantToUseATemplate")}
            <ReportTypeSelector disabled={exportType === ExportType.Template} />
          </div>
        )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exportType, templates]
  );

  const handleRadioButtonChange = (item: Item<ExportType>) => {
    updateData({
      exportType: item.id,
      selectedFields:
        item.id === ExportType.Custom
          ? []
          : flatten(values(selectedTemplate?.fields).map((val) => values(val))),
      selectedFilters:
        item.id === ExportType.Custom ? {} : decamelizeFilters(selectedTemplate?.filters || {}),
      frequency:
        item.id === ExportType.Template && selectedTemplate
          ? getFrequencyByValue(selectedTemplate.aggregateBy)
          : Frequency.Daily
    });
  };

  return (
    <div className={css.root}>
      <p className={css.label}>{t("wouldYouLikeToUseATemplate")}</p>
      <Radios<ExportType>
        items={items}
        value={exportType}
        handleChange={handleRadioButtonChange}
        itemClassName={css.radioItem}
      />
    </div>
  );
};
